/**
 * P2 (AN-PP2): elegibilidade única, versionada e explicável — contrato comum do front.
 *
 * Espelha `br.com.originei.common.elegibilidade` (motor único em originei-common). A tela NUNCA
 * calcula elegibilidade: mostra o que o servidor devolve (`POST /v1/oportunidades/elegibilidade/avaliar`,
 * `GET /v1/oportunidades/{id}/elegibilidade`, alertas da simulação). Pela Q66 nada aqui desabilita
 * produto nem impede cadastro — só o Encaminhamento ao parceiro é impedido, no servidor.
 *
 * Formas de resposta específicas de oportunidades/produtos (snapshot, decisões, política por produto)
 * seguem o desenho §2.3/§5; DEV-PP2-O/P confirmam os nomes na integração.
 */

// ---------------------------------------------------------------------------------------------
// Enums do motor (mesmos nomes do Java)
// ---------------------------------------------------------------------------------------------

/** `TipoElegibilidade` — nunca nulo (PDF 25/10 §10). */
export type TipoElegibilidade = 'REVENUE_MIN' | 'RECEIVABLES_RANGE' | 'LTV_MAX' | 'MANUAL_REVIEW'

export const TIPOS_ELEGIBILIDADE: readonly TipoElegibilidade[] = [
  'REVENUE_MIN',
  'RECEIVABLES_RANGE',
  'LTV_MAX',
  'MANUAL_REVIEW',
]

/** `ResultadoElegibilidade`. */
export type ResultadoElegibilidade =
  | 'ATENDE'
  | 'NAO_ATENDE'
  | 'DADOS_INSUFICIENTES'
  | 'AVALIACAO_ORIGINEI'
  | 'SEM_POLITICA'

/** `RotaOperacao` — só produtos `RECEIVABLES_RANGE` têm rota. */
export type RotaOperacao = 'PADRAO' | 'GRANDES_OPERACOES'

/**
 * Resultados que impedem o Encaminhamento ao parceiro (Q66) — mesmo conjunto de
 * `ResultadoElegibilidade.impedeEncaminhamento()`. Só para explicar na tela; quem decide é o servidor
 * (422 `ELEGIBILIDADE_IMPEDE_ENCAMINHAMENTO`).
 */
export const RESULTADOS_QUE_IMPEDEM_ENCAMINHAMENTO: ReadonlySet<ResultadoElegibilidade> =
  new Set<ResultadoElegibilidade>(['NAO_ATENDE', 'DADOS_INSUFICIENTES', 'SEM_POLITICA'])

// ---------------------------------------------------------------------------------------------
// Códigos estáveis (`MotorElegibilidade.*`)
// ---------------------------------------------------------------------------------------------

export const CODIGOS_ELEGIBILIDADE = {
  FATURAMENTO_NAO_INFORMADO: 'FATURAMENTO_NAO_INFORMADO',
  FATURAMENTO_ABAIXO_MINIMO: 'FATURAMENTO_ABAIXO_MINIMO',
  VALOR_OPERACAO_NAO_INFORMADO: 'VALOR_OPERACAO_NAO_INFORMADO',
  VALOR_OPERACAO_ABAIXO_MINIMO: 'VALOR_OPERACAO_ABAIXO_MINIMO',
  ENCAMINHAR_GRANDES_OPERACOES: 'ENCAMINHAR_GRANDES_OPERACOES',
  LTV_NAO_INFORMADO: 'LTV_NAO_INFORMADO',
  LTV_ACIMA_MAXIMO: 'LTV_ACIMA_MAXIMO',
  AVALIACAO_ORIGINEI: 'ELEGIBILIDADE_AVALIACAO_ORIGINEI',
  SEM_POLITICA: 'ELEGIBILIDADE_SEM_POLITICA',
} as const

export type CodigoElegibilidade = (typeof CODIGOS_ELEGIBILIDADE)[keyof typeof CODIGOS_ELEGIBILIDADE]

/** Códigos de erro HTTP da P2 (corpo `ApiResponse.codigo`). */
export const ERROS_ELEGIBILIDADE = {
  IMPEDE_ENCAMINHAMENTO: 'ELEGIBILIDADE_IMPEDE_ENCAMINHAMENTO',
  CONGELADA: 'ELEGIBILIDADE_CONGELADA',
  SEM_IMPEDIMENTO: 'ELEGIBILIDADE_SEM_IMPEDIMENTO',
  AVALIACAO_MUDOU: 'AVALIACAO_MUDOU',
  ROTA_NAO_APLICAVEL: 'ROTA_NAO_APLICAVEL',
  MOTIVO_OBRIGATORIO: 'MOTIVO_OBRIGATORIO',
  PRODUTO_DESCONHECIDO: 'PRODUTO_DESCONHECIDO',
  /** 404 da avaliação avulsa para o consultor: inativo e inexistente respondem igual. */
  PRODUTO_NAO_ENCONTRADO: 'PRODUTO_NAO_ENCONTRADO',
  CADASTRADA_EM_PRODUTOS: 'ELEGIBILIDADE_CADASTRADA_EM_PRODUTOS',
  PARAMETROS_INCOMPATIVEIS_COM_TIPO: 'PARAMETROS_INCOMPATIVEIS_COM_TIPO',
  TIPO_FIXO_DO_PRODUTO: 'TIPO_FIXO_DO_PRODUTO',
  VIGENCIA_RETROATIVA: 'VIGENCIA_RETROATIVA',
  VIGENCIA_CONFLITANTE: 'VIGENCIA_CONFLITANTE',
  ORIGEM_OBRIGATORIA: 'ORIGEM_OBRIGATORIA',
  TIPO_OBRIGATORIO: 'TIPO_OBRIGATORIO',
  PRODUTO_RETIRADO: 'PRODUTO_RETIRADO',
  JA_SOBREPOSTA: 'ELEGIBILIDADE_JA_SOBREPOSTA',
  DADOS_NAO_EDITAVEIS: 'DADOS_ELEGIBILIDADE_NAO_EDITAVEIS',
  VALOR_IMOVEL_FONTE_INVALIDA: 'VALOR_IMOVEL_FONTE_INVALIDA',
  VALOR_INVALIDO: 'VALOR_ELEGIBILIDADE_INVALIDO',
  ROTA_OBRIGATORIA: 'ROTA_OBRIGATORIA',
  ROTA_NAO_ALTERAVEL: 'ROTA_NAO_ALTERAVEL',
  ROTA_INALTERADA: 'ROTA_INALTERADA',
} as const

// ---------------------------------------------------------------------------------------------
// Saída do motor
// ---------------------------------------------------------------------------------------------

/** `Explicacao` — `limite`/`valorInformado` em BRL, ou FRAÇÃO quando o parâmetro é `ltvMaximo`. */
export interface ExplicacaoElegibilidade {
  codigo: CodigoElegibilidade | string
  mensagem: string
  parametro?: 'faturamentoAnualMinimo' | 'valorOperacaoMinimo' | 'valorGrandesOperacoes' | 'ltvMaximo' | null
  limite?: number | null
  valorInformado?: number | null
  consequencia?: string | null
}

/** `AvaliacaoElegibilidade`. `ltvCalculado` é FRAÇÃO com 6 casas, só exibição — mostrar como veio. */
export interface AvaliacaoElegibilidade {
  resultado: ResultadoElegibilidade
  impedeEncaminhamento: boolean
  rota?: RotaOperacao | null
  ltvCalculado?: number | null
  explicacoes: ExplicacaoElegibilidade[]
  motorVersao: string
}

// ---------------------------------------------------------------------------------------------
// Política (fonte: produtos V14) — `GET /v1/produtos/{id}/politicas-elegibilidade`
// ---------------------------------------------------------------------------------------------

/** Parâmetros por tipo; os que não são do tipo vêm nulos. `ltvMaximo` como FRAÇÃO. */
export interface ParametrosElegibilidade {
  faturamentoAnualMinimo?: number | null
  valorOperacaoMinimo?: number | null
  valorGrandesOperacoes?: number | null
  ltvMaximo?: number | null
}

export interface PoliticaElegibilidade extends ParametrosElegibilidade {
  id: string
  produtoId: string
  produtoCodigo: string
  versao: number
  tipo: TipoElegibilidade
  /** ISO `YYYY-MM-DD`. */
  vigenciaInicio: string
  /** ISO `YYYY-MM-DD`; omitido = sem fim (aberta). */
  vigenciaFim?: string | null
  /** Vigente HOJE (calculado no servidor) — uma versão aberta com início futuro não é vigente. */
  vigente?: boolean | null
  /** Só back-office (o consultor recebe a forma resumida, sem origem/motivo/autoria). */
  origem?: string | null
  /** Só back-office. */
  motivo?: string | null
  hashRegra?: string | null
  sequencia?: number | null
  criadoPor?: string | null
  criadoEm?: string | null
  // ─── P6 (AN-PP6 §3, produtos V15 parte A2): publicação com dupla aprovação ───
  /** PENDENTE_APROVACAO (proposta; não vale) | ATIVA | REJEITADA. Nas duas formas. */
  status?: 'PENDENTE_APROVACAO' | 'ATIVA' | 'REJEITADA' | 'ENCERRADA' | 'RASCUNHO' | string | null
  /** Área dona (`AreaDona`); elegibilidade: COMERCIAL. */
  owner?: string | null
  /** ATIVA anterior à dupla aprovação (sem aprovador). */
  legado?: boolean | null
  /** Só back-office (o consultor nunca vê pessoas). `sub` do proponente. */
  propostoPor?: string | null
  aprovadoPor?: string | null
  aprovadoEm?: string | null
  rejeitadoPor?: string | null
  rejeitadoEm?: string | null
  motivoRejeicao?: string | null
  /** Concorrência otimista: vai no corpo de aprovar/rejeitar (409 `CONFLITO_VERSAO`). Só back-office. */
  versaoRegistro?: number | null
}

/**
 * `GET /v1/produtos/{id}/politicas-elegibilidade` → `PoliticasElegibilidadeProdutoResponse`.
 * Consultor: `historico` = só a vigente (ou vazio). Back-office: todas, versão desc.
 */
export interface PoliticasElegibilidadeProduto {
  produtoId: string
  produtoCodigo?: string | null
  /** Omitida quando não há política vigente hoje (avaliação = "Política não sincronizada"). */
  vigente?: PoliticaElegibilidade | null
  historico: PoliticaElegibilidade[]
}

/** `POST /v1/produtos/{id}/politicas-elegibilidade` (ADMIN) — nova versão. */
export interface NovaPoliticaElegibilidadeRequest extends ParametrosElegibilidade {
  tipo: TipoElegibilidade
  /** ISO `YYYY-MM-DD`; ausente = hoje; passado → 422 `VIGENCIA_RETROATIVA`. */
  vigenciaInicio?: string | null
  origem: string
  /** Obrigatório (422 `MOTIVO_OBRIGATORIO`). */
  motivo: string
  /** P6: área dona; omitido = COMERCIAL (a única aceita para elegibilidade). */
  owner?: string | null
}

/** Parâmetros exigidos por tipo (mesmo CHECK da V14) — para montar o formulário. */
export const PARAMETROS_POR_TIPO: Readonly<Record<TipoElegibilidade, readonly (keyof ParametrosElegibilidade)[]>> = {
  REVENUE_MIN: ['faturamentoAnualMinimo'],
  RECEIVABLES_RANGE: ['valorOperacaoMinimo', 'valorGrandesOperacoes'],
  LTV_MAX: ['ltvMaximo'],
  MANUAL_REVIEW: [],
}

/** `OTHER_NEED` é sempre `MANUAL_REVIEW` (422 `TIPO_FIXO_DO_PRODUTO`). */
export const PRODUTOS_COM_TIPO_FIXO: Readonly<Record<string, TipoElegibilidade>> = {
  OTHER_NEED: 'MANUAL_REVIEW',
}

// ---------------------------------------------------------------------------------------------
// Entradas declaradas (Q69) e avaliação sem persistir
// ---------------------------------------------------------------------------------------------

/** Fonte do valor do imóvel (Q-PP2-4). */
export type FonteValorImovel = 'ESTIMATIVA_CONSULTOR' | 'INFORMADO_CLIENTE' | 'LAUDO_AVALIACAO'

/** Origem do faturamento gravado no snapshot. */
export type OrigemFaturamento = 'DECLARADO_CONSULTOR' | 'TRANSCRITO_BACKOFFICE'

/**
 * Dados declarados da reserva — `CriarOportunidadeRequest.dadosElegibilidade` e
 * `PUT /v1/oportunidades/{id}/elegibilidade/dados`. O valor da operação é o `valorEstimado` da reserva.
 */
export interface DadosDeclaradosElegibilidade {
  faturamentoAnual?: number | null
  valorImovel?: number | null
  valorImovelFonte?: FonteValorImovel | null
  valorSolicitado?: number | null
}

/** `POST /v1/oportunidades/elegibilidade/avaliar` — não persiste. Sem `produtoId` = todos os utilizáveis. */
export interface AvaliarElegibilidadeRequest {
  produtoId?: string | null
  faturamentoAnual?: number | null
  /** Q24: base da Antecipação (1MM / 30MM). */
  valorOperacao?: number | null
  valorImovel?: number | null
  valorSolicitado?: number | null
}

/** Uma linha da avaliação por produto (chips do seletor). */
export interface AvaliacaoElegibilidadeProduto extends AvaliacaoElegibilidade {
  produtoId: string
  produtoCodigo: string
  produtoNome?: string | null
  /** Nulos em `SEM_POLITICA`. */
  politicaId?: string | null
  politicaVersao?: number | null
  politicaTipo?: TipoElegibilidade | null
  politicaVigenciaInicio?: string | null
}

/** `POST /v1/oportunidades/elegibilidade/avaliar` → `AvaliarElegibilidadeResponse`. */
export interface AvaliarElegibilidadeResponse {
  aviso?: string | null
  avaliacoes: AvaliacaoElegibilidadeProduto[]
}

// ---------------------------------------------------------------------------------------------
// Snapshot e decisões na reserva (oportunidades V61)
// ---------------------------------------------------------------------------------------------

export type MomentoAvaliacaoElegibilidade =
  | 'CRIACAO'
  | 'ATUALIZACAO_DADOS'
  | 'ALTERACAO_VALOR'
  | 'TROCA_PRODUTO'
  | 'RECLASSIFICACAO'
  | 'REAVALIACAO'
  | 'SOBREPOSICAO'
  | 'ENCAMINHAMENTO'

export type OrigemRota = 'AUTOMATICA' | 'SOBREPOSTA_ADMIN'

export type TipoDecisaoElegibilidade = 'SOBREPOR_IMPEDIMENTO' | 'ALTERAR_ROTA'

/** Tamanho máximo do motivo de sobreposição/alteração de rota (`VARCHAR(500)`). */
export const TAMANHO_MAXIMO_MOTIVO_ELEGIBILIDADE = 500

// ---------------------------------------------------------------------------------------------
// Rótulos (nomes do desenho §8)
// ---------------------------------------------------------------------------------------------

export const ROTULO_TIPO_ELEGIBILIDADE: Readonly<Record<TipoElegibilidade, string>> = {
  REVENUE_MIN: 'Faturamento mínimo',
  RECEIVABLES_RANGE: 'Faixa de valor da operação',
  LTV_MAX: 'LTV máximo',
  MANUAL_REVIEW: 'Avaliação Originei',
}

export const ROTULO_RESULTADO_ELEGIBILIDADE: Readonly<Record<ResultadoElegibilidade, string>> = {
  ATENDE: 'Atende',
  NAO_ATENDE: 'Alerta de elegibilidade',
  DADOS_INSUFICIENTES: 'Dados insuficientes',
  AVALIACAO_ORIGINEI: 'Avaliação Originei',
  SEM_POLITICA: 'Política não sincronizada',
}

export const ROTULO_ROTA_OPERACAO: Readonly<Record<RotaOperacao, string>> = {
  PADRAO: 'Padrão',
  GRANDES_OPERACOES: 'Grandes Operações',
}

export const ROTULO_FONTE_VALOR_IMOVEL: Readonly<Record<FonteValorImovel, string>> = {
  ESTIMATIVA_CONSULTOR: 'Estimativa do consultor',
  INFORMADO_CLIENTE: 'Informado pelo cliente',
  LAUDO_AVALIACAO: 'Laudo de avaliação',
}

/** `MotorElegibilidade.AVISO_NAO_E_APROVACAO` — rodapé fixo de todo alerta. */
export const AVISO_ELEGIBILIDADE_NAO_E_APROVACAO = 'Elegibilidade não equivale a aprovação de crédito.'

/** Texto do painel `AlertaElegibilidade` na Nova Reserva (§8). */
export const TEXTO_ALERTA_ELEGIBILIDADE_RESERVA =
  'Elegibilidade não é aprovação de crédito. O alerta não impede o cadastro; impede o encaminhamento ao ' +
  'parceiro até ajuste ou decisão da Originei.'

/** Tooltip do chip "Avaliação Originei" (MANUAL_REVIEW). */
export const TOOLTIP_AVALIACAO_ORIGINEI = 'A Originei decide; nenhum requisito é presumido.'
